import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfig } from '../../../_helpers/appconfig.service';
import { map } from 'rxjs/operators';
import { BillingFileFormat } from '../new-version/billing.constants';

export interface BillingDocument {
  billing_type: string;
  correlative: number;
  issue_date: string;
  serie: string;
  status: string;
}

export interface BillingResponse {
  data: BillingDocument[];
  has_more: boolean;
  object: string;
  records: number;
}

export interface DownloadRequest {
  customer_public_id: string;
  numero: number;
  serie: string;
  billing_type: string;
  file_format: string;
}

export interface DownloadResponse {
  blob: Blob;
  filename: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {

  private apiUrl = AppConfig.settings.apiServer.kcmssvcURL.toString();

  constructor(private http: HttpClient) { }

  /**
   * Obtiene los documentos de facturación filtrados por cliente, rango de fechas, término de búsqueda, tipo de documento y límite de registros.
   * @param customerPublicId - El ID público del cliente.
   * @param startDate - Fecha de inicio en formato 'YYYY-MM-DD HH:mm:ss'.
   * @param endDate - Fecha de fin en formato 'YYYY-MM-DD HH:mm:ss'.
   * @param search - Término de búsqueda opcional.
   * @param billingType - Tipo de documento opcional.
   * @param limit - Número de registros por página.
   * @returns Observable con la lista de documentos de facturación.
   */
  getBillingDocuments(
    customerPublicId: string, 
    startDate: string, 
    endDate: string, 
    search?: string,
    billingType?: string,
    limit: number = 10
  ): Observable<BillingResponse> {
    let params = new HttpParams()
      .set('customer_public_id', customerPublicId)
      .set('start_date', startDate)
      .set('end_date', endDate)
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    if (billingType) {
      params = params.set('billing_type', billingType);
    }

    const url = `${this.apiUrl}/billing_documents`;

    return this.http.get<BillingResponse>(url, { params });
  }

  /**
   * Descarga un documento de facturación.
   * @param customerPublicId - El ID público del cliente.
   * @param document - El documento a descargar.
   * @param fileFormat - El formato del archivo (PDF, XML, CDR).
   * @returns Observable con la respuesta de la descarga.
   */
  downloadBillingDocument(customerPublicId: string, document: BillingDocument, fileFormat: string): Observable<DownloadResponse> {
    const url = `${this.apiUrl}/billing_documents/download`;
    
    // Crear el objeto de solicitud con los datos exactos requeridos
    const requestBody: DownloadRequest = {
      customer_public_id: customerPublicId,
      numero: document.correlative,
      serie: document.serie,
      billing_type: document.billing_type,
      file_format: fileFormat
    };

    // Enviar la solicitud POST con el body correcto
    return this.http.post(url, requestBody, { 
      responseType: 'blob',
      observe: 'response',
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      map(response => {
        const contentDisposition = response.headers.get('content-disposition');
        const filename = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : null;
        
        // Si el formato es XML o CDR, cambiar la extensión a ZIP
        if (fileFormat.toLowerCase() === BillingFileFormat.XML || fileFormat.toLowerCase() === BillingFileFormat.CDR) {
          const zipBlob = new Blob([response.body], { 
            type: 'application/zip'
          });
          return {
            blob: zipBlob,
            filename: `${document.serie}-${document.correlative}.zip`
          };
        }
        
        return { blob: response.body, filename };
      })
    );
  }
}
